import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Axios interceptor: auto-retry once with refresh token on 401
let isRefreshing = false;
let pendingRequests = [];

axios.interceptors.response.use(
    (res) => res,
    async (error) => {
        const original = error.config;
        if (
            error.response?.status === 401 &&
            !original._retry &&
            !original.url.includes('/auth/login') &&
            !original.url.includes('/auth/register') &&
            !original.url.includes('/auth/refresh')
        ) {
            original._retry = true;
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingRequests.push({ resolve, reject, original });
                });
            }
            isRefreshing = true;
            try {
                await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
                pendingRequests.forEach(({ resolve, original: req }) => resolve(axios(req)));
                pendingRequests = [];
                return axios(original);
            } catch (e) {
                pendingRequests.forEach(({ reject }) => reject(e));
                pendingRequests = [];
                throw e;
            } finally {
                isRefreshing = false;
            }
        }
        throw error;
    }
);

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

function formatApiErrorDetail(detail) {
    if (detail == null) return "Er is iets misgegaan. Probeer het opnieuw.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch (e) {
            // /auth/me already triggers the interceptor refresh attempt; if still failing, user is logged out
            setUser(false);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password, rememberMe = false) => {
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe }, { withCredentials: true });
            setUser(data);
            // Reset daily-question gate so a fresh login always shows the CBR question
            try {
                Object.keys(sessionStorage)
                    .filter(k => k.startsWith('cbr_question_') || k.startsWith('lesson_summary_shown_'))
                    .forEach(k => sessionStorage.removeItem(k));
            } catch {}
            return { success: true, user: data };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (email, password, name, role = 'student') => {
        try {
            const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role }, { withCredentials: true });
            setUser(data);
            return { success: true };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (e) {
            console.error('Logout error:', e);
        }
        setUser(false);
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isInstructor: user?.role === 'instructor',
        isStudent: user?.role === 'student',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
